<template>
    <div v-if="canUpdate">
        <settings-heading
            :title="$t('settings.tracking.title')"
            :description="$t('settings.tracking.description')"
        />

        <h2 class="first-letter:uppercase text-heading-2 font-semibold mr-auto mb-16">{{ $t('settings.tracking.gtagmanager') }}</h2>
        <p class="text-primary-80 mb-16 mw-" v-html="$t('settings.tracking.container_description')"></p>

        <simple-input
            v-model="form.gtm_container_id"
            autofocus
            class="mb-32"
            mode="css"
            placeholder="GTM-XXXXXX"
            wrapper-class="max-w-240"
        >
            {{ $t('settings.tracking.container') }}
        </simple-input>

        <banner
            :description="$t('settings.tracking.warning')"
            inline
            v-if="form.gtm_container_id"
            warning
        />


        <h2 class="first-letter:uppercase text-heading-2 font-semibold mr-auto mb-16">{{ $t('settings.tracking.ganalytics') }}</h2>
        <p class="text-primary-80 mb-16" v-html="$t('settings.tracking.description')"></p>
        <simple-input
            v-model="form.ga_container_id"
            autofocus
            class="mb-32"
            mode="css"
            placeholder="G-XXXXXX-1"
            wrapper-class="max-w-240"
        >
            {{ $t('settings.tracking.analytics') }}
        </simple-input>

        <hr class="mb-32 border-t border-primary-20">

        <div class="space-y-16 my-32">
            <h2 class="first-letter:uppercase text-heading-2 text-black font-semibold mr-auto">{{ $t('settings.tracking.cookie_banner.title') }}</h2>
            <p class="text-black">{{ $t('settings.tracking.cookie_banner.description') }}</p>

            <toggle
                :checked="form.display_cookie_banner"
                :disabled="form.share_anonymous_data"
                class="flex-none"
                @change="form.display_cookie_banner = $event"
            >
                {{ $t('settings.privacy.gdpr.label') }}
            </toggle>

            <toggle v-model="form.share_anonymous_data">
                {{ $t('settings.tracking.share_anonymous_data') }}
            </toggle>

            <banner
                :description="$t('settings.tracking.cookie_banner.warning.description')"
                inline
                info
                v-if="form.display_cookie_banner && usesDefaultCookieBanner"
            />
        </div>

        <translatable v-if="form.display_cookie_banner" #default="{ languages, selectedLanguage }" :errors="warningTabs('cookie_banner')" class="w-full mb-32">
            <div v-for="lang in languages" :key="lang">
                <div v-show="lang === selectedLanguage">
                    <wysiwyg
                        v-if="!loadingData"
                        :ref="'wysiwyg_' + lang"
                        v-model="form.cookie_banner[lang]"
                        :autofocus="lang === selectedLanguage"
                        full-featured
                    >
                        {{ $t('settings.tracking.cookie_banner.wysiwyg.description') }}
                        <template #after>
                            <div class="border-t border-primary-60 bg-primary-5 flex items-start gap-12 p-12">
                                <p class="text-primary-80 flex-1 cursor-default" v-html="$t('jobsite.cookie_consent.message.secondary', lang, {link: getCookiePolicyLink(lang)})"></p>
                                <tooltip
                                    :content="$t('settings.tracking.cookie-banner.policy-message.tooltip')"
                                    position="left"
                                    tooltip-width="w-200 md:w-240 lg:w-300"
                                >
                                    <svg-icon type="info" class="fill-semantic-info"/>
                                </tooltip>
                            </div>
                        </template>
                    </wysiwyg>
                    <loading v-else/>
                </div>
            </div>
        </translatable>

        <hr class="mb-32 border-t border-grey-lighter">

        <h2 class="first-letter:uppercase text-heading-2 text-black font-semibold mr-auto mb-16 mt-32">{{ $t('settings.tracking.cookie_policy.title') }}</h2>
        <p class="mb-16 text-black" v-html="$t('settings.tracking.cookie_policy.description')"></p>
        <translatable #default="{ languages, selectedLanguage }" :errors="warningTabs('cookie_policy_file')" class="w-full mb-32">
            <div v-for="lang in languages" :key="lang">
                <div v-show="lang === selectedLanguage">
                    <div class="my-16" v-if="!loadingData">
                        <p class="text-body-sm first-letter:uppercase font-semibold text-black pb-8">
                            {{ $t('settings.tracking.cookie_policy.upload') }}
                        </p>
                        <div
                            v-if="(attachments[lang] && attachments[lang].file_url)"
                            class="relative shadow rounded"
                        >
                            <a
                                :download="extensions.includes(attachments[lang].extension ? attachments[lang].original_name : null)"
                                :href="attachments[lang] ? attachments[lang].file_url : null"
                                target="_blank"
                                class="group flex items-center p-16 cursor-pointer"
                            >
                                <p class="mr-auto font-semibold group-hover:text-primary trans-ease">{{ attachments[lang].original_name}}</p>
                                <div class="pl-16">
                                    <div class="h-32 w-32 flex items-center justify-center rounded-sm text-black trans-ease group-hover:text-primary mr-32">
                                        <svg-icon type="download" class="max-w-16 max-h-16"></svg-icon>
                                    </div>
                                </div>
                            </a>
                            <div class="absolute right-0 inset-y-0 mr-16 flex items-center pointer-events-none">
                                <button
                                    class="text-black hover:text-semantic-error cursor-pointer trans-ease pointer-events-auto h-32 w-32 flex items-center justify-center rounded"
                                    @click="confirmDeleteAttachment(lang)"
                                >
                                    <svg-icon type="delete" class="max-w-16 max-h-16"></svg-icon>
                                </button>
                            </div>
                        </div>
                        <file-upload
                            v-else
                            v-model="form.cookie_policy_queued_attachments[lang]"
                            :ref="'cookie_policy_' + lang"
                            :allData="true"
                            :multiple="false"
                            class="w-full rounded"
                            icon-location="corner"
                            only-accept="application/pdf"
                        />
                    </div>
                    <loading v-else/>
                </div>
            </div>
        </translatable>

        <div class="flex justify-end mb-48">
            <simple-button
                :disabled="!form.isDirty()"
                display="secondary"
                icon="save"
                :loading="form.submitting"
                look="primary"
                @click="submitForm"
            >
                {{ $t('settings.save') }}
            </simple-button>
        </div>

        <popup-modal-confirm
            v-if="deletingAttachment"
            :confirm-text="$t('general.delete')"
            :title="$t('settings.tracking.cookie_policy.delete.title')"
            :message="$t('general.confirm_delete_subject', {
                subject: ($t('settings.tracking.cookie_policy.delete.message') + ' ' + this.attachments[deletingAttachment].original_name)
            })"
            :working="performingDeleteAttachmentRequest"
            show-exclamation
            @close="cancelDeleteAttachment"
            @confirm="deleteAttachment"
        />

    </div>
    <error-403 v-else></error-403>
</template>

<script>
import SimpleButton from '../../components/ui/SimpleButton.vue';
import Input from '../../components/forms/Input.vue';
import Form from '../../etc/forms/Form';
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useNotificationsStore} from '../../state/modules/notifications';
import * as jobsiteService from '../../services/JobsiteService';
import Jobsite from '../../models/Jobsite';
import {ADD_NOTIFICATION} from '../../state/mutation-types';
import {normalizeJson, openableFileExtensionsInWebBrowser} from '../../../shared/helpers';
import confirmLeave from '../../mixins/confirmLeave';
import Error403 from '../../components/ui/Error403.vue';
import Toggle from '../../components/forms/Toggle.vue';
import Wysiwyg from '../../components/forms/Wysiwyg';
import Translatable from '../../components/forms/Translatable';
import SvgIcon from '../../components/ui/SvgIcon';
import FileUpload from "../../components/forms/FileUpload.vue";
import PopupModalConfirm from "../../components/ui/PopupModalConfirm.vue";
import Alert from "../../components/ui/Alert.vue";
import Loading from "../../components/ui/Loading.vue";
import SettingsHeading from "../../components/ui/SettingsHeading.vue";
import Banner from "../../components/ui/Banner.vue";
import Tooltip from "../../components/navigation/Tooltip.vue";

export default {
    components: {
        Tooltip,
        Loading,
        Alert,
        PopupModalConfirm,
        FileUpload,
        Banner,
        SettingsHeading,
        SvgIcon,
        Translatable,
        Wysiwyg,
        'simple-button': SimpleButton,
        'simple-input': Input,
        'error-403': Error403,
        'toggle': Toggle,
    },
    mixins: [
        confirmLeave,
    ],
    setup() {
        const mainStore = useMainStore();
        return {mainStore};
    },
    data: function () {
        return {
            form: this.generateForm(),
            canUpdate: this.$can('settings.jobsite'),
            attachments: {},
            extensions: openableFileExtensionsInWebBrowser,
            deletingAttachment: null,
            performingDeleteAttachmentRequest: false,
            loadingData: false,
        };
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany']),
        cookieBannerText() {
            let data = {};
            this.currentCompany.jobsite.active_languages.forEach(lang => {
                if (this.currentCompany.jobsite.cookie_banner[lang]) {
                    data[lang] = this.currentCompany.jobsite.cookie_banner[lang];
                } else {
                    data[lang] = this.defaultCookieBannerText(lang);
                }
            })
            return data;
        },
        usesDefaultCookieBanner() {
            let defaultText = false;
            Object.keys(this.form.cookie_banner).forEach(lang => {
                if (this.form.cookie_banner[lang] === this.defaultCookieBannerText(lang)) {
                    defaultText = true;
                }
            });
            return defaultText;
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        defaultCookieBannerText(lang) {
            return this.$tc('jobsite.cookie_consent.message.primary', 1, lang, {partners: 1});
        },
        warningTabs(attribute) {
            let languages = this.currentCompany.jobsite.active_languages;
            let errorLang = [];

            for (let index in languages) {
                if (attribute === 'cookie_policy_file') {
                    if ((!this.attachments[languages[index]] || !this.attachments[languages[index]].file_url) &&
                        (!this.form.data.cookie_policy_queued_attachments || !this.form.data.cookie_policy_queued_attachments[languages[index]])) {
                        errorLang.push(languages[index]);
                    }
                } else if (!this.form.data[attribute] || !this.form.data[attribute][languages[index]]) {
                    errorLang.push(languages[index]);
                }
            }
            return errorLang;
        },
        generateForm() {
            return new Form([
                {
                    name: 'gtm_container_id',
                    value: this.mainStore.currentCompany.jobsite.gtm_container_id,
                },
                {
                    name: 'ga_container_id',
                    value: this.mainStore.currentCompany.jobsite.ga_container_id,
                },
                {
                    name: 'share_anonymous_data',
                    value: this.mainStore.currentCompany.jobsite.share_anonymous_data,
                },
                {
                    name: 'display_cookie_banner',
                    value: this.mainStore.currentCompany.jobsite.display_cookie_banner,
                },
                {
                    name: 'cookie_banner',
                    value: this.cookieBannerText,
                },
                {
                    name: 'cookie_policy_queued_attachments',
                    value: {}
                },
            ]);
        },
        async submitForm() {
            this.form.submitting = true;

            await this.form.submit(data => {
                Object.entries(data.cookie_banner).forEach(([lang, text]) => {
                    if (text === this.defaultCookieBannerText(lang)) {
                        data.cookie_banner[lang] = null;
                    }
                });
                return jobsiteService.update(this.currentCompany.id, {
                    data: {attributes: data},
                });
            }).then(result => {
                return useRepo(Jobsite)
                    .whereId(result.id)
                    .update(normalizeJson(result));
            }).then(async () => {
                this.form = this.generateForm();
                await this.refetchData();
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('settings.saved', {subject: this.$t('settings.tracking.title')}),
                })
            }).finally(() => {
                // check if some languages have a cookie banner, and if so, and not all languages have a cookie banner, throw a notification
                let cookieBannerEntries = Object.entries(this.form.cookie_banner);
                let someHaveCookieBanner = cookieBannerEntries.some(([lang, content]) => {
                    return content && content !== this.defaultCookieBannerText(lang);
                });
                let allHaveCookieBanner = cookieBannerEntries.every(([lang, content]) => {
                    return content && content !== this.defaultCookieBannerText(lang);
                });
                let cookieBannerWarning = someHaveCookieBanner && !allHaveCookieBanner;

                // check if some languages have a cookie policy, and if so, and not all languages have a cookie policy, throw a notification
                let cookiePolicyEntries = Object.entries(this.attachments);
                let someHaveCookiePolicy = cookiePolicyEntries.some(([lang, attachment]) => {
                    return attachment.file_url;
                });
                let allHaveCookiePolicy = cookiePolicyEntries.every(([lang, attachment]) => {
                    return attachment.file_url;
                });
                let cookiePolicyWarning = someHaveCookiePolicy && !allHaveCookiePolicy;

                if (cookieBannerWarning) {
                    this[ADD_NOTIFICATION]({
                        type: 'warning',
                        title: this.$t('settings.tracking.cookie_banner.other_languages_warning.title'),
                        message: this.$t('settings.tracking.cookie_banner.other_languages_warning.description'),
                    })
                }

                if (cookiePolicyWarning) {
                    this[ADD_NOTIFICATION]({
                        type: 'warning',
                        title: this.$t('settings.tracking.cookie_policy.other_languages_warning.title'),
                        message: this.$t('settings.tracking.cookie_policy.other_languages_warning.description'),
                    })
                }
            })

            this.form.submitting = false;
        },
        refetchData() {
            return new Promise((resolve, reject) => {
                this.loadingData = true;
                jobsiteService.show(this.currentCompany.id).then(result => {
                    return useRepo(Jobsite).save(normalizeJson(result.data));
                }).then(result => {
                    if (this.form.isDifferent(result)) {
                        this.form = this.generateForm();
                    }
                }).then(() => {
                    this.attachments = this.setAttachments();
                }).catch(reject).finally(() => {
                    this.loadingData = false;
                    resolve();
                });
            });
        },
        initAttachments() {
            let langs = {}
            this.currentCompany.jobsite.active_languages.forEach(lang => {
                langs[lang] = null;
            });
            return langs;
        },
        setAttachments() {
            let result = this.initAttachments();
            Object.keys(result).forEach(lang => {
                result[lang] = {
                    'file_name': this.currentCompany.jobsite.cookie_policy_file_name[lang],
                    'original_name': this.currentCompany.jobsite.cookie_policy_file_original_name[lang],
                    'file_url': this.currentCompany.jobsite.cookie_policy_file_url[lang],
                    'extension': this.currentCompany.jobsite.cookie_policy_file_extension[lang],
                };
            });
            return result;
        },
        confirmDeleteAttachment(lang) {
            this.deletingAttachment = lang;
        },
        cancelDeleteAttachment() {
            this.deletingAttachment = null;
        },
        deleteAttachment() {
            this.performingDeleteAttachmentRequest = true;
            jobsiteService.deleteCookiePolicyFile(this.currentCompany.id, {
                cookie_policy_file: this.deletingAttachment,
            }).then((res) => {
                this.deletingAttachment = null;
                this.refetchData();
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: res.message,
                });
            }).finally(() => {
                this.performingDeleteAttachmentRequest = false;
            })
        },
        getCookiePolicyLink(lang) {
            let companyDefaultLang = this.currentCompany.jobsite.default_lang;
            if (this.attachments[lang]?.file_url) {
                return this.attachments[lang].file_url;
            } else if (this.attachments[companyDefaultLang]?.file_url) {
                return this.attachments[companyDefaultLang].file_url;
            } else {
                // check to see if there are any file urls at all, if so, return the first one, otherwise return the default cookie link
                let fileUrls = Object.values(this.attachments).map(attachment => attachment.file_url);
                return fileUrls.find(url => url) || this.$t('jobsite.jobtoolz_cookie_link', lang);
            }
        }
    },
    watch: {
        'form.share_anonymous_data'() {
            if (!this.form.display_cookie_banner) {
                this.form.display_cookie_banner = true;
            }
        }
    },
    created() {
        this.refetchData();
    },
};
</script>
